// src/utils/aiConfigStorage.ts
// API-based utility for saving and loading AI configuration profiles
// Supports up to 12 saved configurations per user (stored server-side)

import { AISettingsState } from "../components/AISettings.tsx";

const MAX_CONFIGS = 12;

export interface SavedAIConfig {
  id: string;
  name: string;
  description: string;
  settings: AISettingsState;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get the API URL from environment
 */
const getApiUrl = (): string => {
  return process.env.REACT_APP_API_URL || "";
};

/**
 * Get the auth token from localStorage or sessionStorage
 */
const getAuthToken = (): string | null => {
  const token = localStorage.getItem("token");
  if (token) return token;

  const sessionToken = sessionStorage.getItem("token");
  if (sessionToken) return sessionToken;

  return null;
};

/**
 * Helper function to make authenticated API requests
 */
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();
  if (!token) {
    throw new Error("You must be logged in to manage AI configurations.");
  }

  const url = `${getApiUrl()}/api/ai-configs${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  return response;
};

/**
 * Get all saved AI configurations from the server
 */
export const getAIConfigs = async (): Promise<SavedAIConfig[]> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return [];
    }

    const response = await apiRequest("");

    if (!response.ok) {
      console.error("Error fetching AI configs:", response.status);
      return [];
    }

    const data = await response.json();
    return data.configs || [];
  } catch (error) {
    console.error("Error loading AI configs:", error);
    return [];
  }
};

/**
 * Save a new AI configuration to the server
 * Returns the saved config or throws an error
 */
export const saveAIConfig = async (
  name: string,
  description: string,
  settings: AISettingsState
): Promise<SavedAIConfig> => {
  // Validate name
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error("Configuration name cannot be empty.");
  }
  if (trimmedName.length > 50) {
    throw new Error("Configuration name must be 50 characters or less.");
  }

  // Validate description
  const trimmedDescription = description.trim();
  if (trimmedDescription.length > 200) {
    throw new Error("Description must be 200 characters or less.");
  }

  const response = await apiRequest("", {
    method: "POST",
    body: JSON.stringify({
      name: trimmedName,
      description: trimmedDescription,
      settings: settings,
    }),
  });

  // Check response status first before parsing JSON
  if (!response.ok) {
    let errorMessage = "Failed to save configuration.";
    try {
      const errorData = await response.json();
      // Include detail if available for debugging
      errorMessage = errorData.detail
        ? `${errorData.error}: ${errorData.detail}`
        : errorData.error || errorMessage;
    } catch {
      // If response body isn't JSON, use status text
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log(`AI Config saved: "${trimmedName}"`);
  return data.config;
};

/**
 * Load a specific AI configuration by ID
 */
export const getAIConfig = async (configId: string): Promise<SavedAIConfig | null> => {
  const configs = await getAIConfigs();
  return configs.find(c => c.id === configId) || null;
};

/**
 * Update an existing AI configuration
 */
export const updateAIConfig = async (
  configId: string,
  updates: Partial<Pick<SavedAIConfig, "name" | "description" | "settings">>
): Promise<SavedAIConfig> => {
  const response = await apiRequest(`/${configId}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });

  // Check response status first before parsing JSON
  if (!response.ok) {
    let errorMessage = "Failed to update configuration.";
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log(`AI Config updated: "${data.config.name}"`);
  return data.config;
};

/**
 * Delete an AI configuration by ID
 */
export const deleteAIConfig = async (configId: string): Promise<void> => {
  const response = await apiRequest(`/${configId}`, {
    method: "DELETE",
  });

  // Check response status first before parsing JSON
  if (!response.ok) {
    let errorMessage = "Failed to delete configuration.";
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log(`AI Config deleted`);
};

/**
 * Export a configuration to a downloadable JSON file
 */
export const exportAIConfig = (config: SavedAIConfig): void => {
  const exportData = {
    exportedAt: new Date().toISOString(),
    version: "1.0",
    config: config,
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], {
    type: "application/json",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `ai-config-${config.name.replace(/\s+/g, "-").toLowerCase()}-${Date.now()}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  console.log(`AI Config exported: "${config.name}"`);
};

/**
 * Import a configuration from a JSON file
 * Returns the imported config or throws an error
 */
export const importAIConfig = async (file: File): Promise<SavedAIConfig> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const content = event.target?.result as string;
        const data = JSON.parse(content);

        // Validate the imported data
        if (!data.config || !data.config.settings) {
          throw new Error("Invalid configuration file format.");
        }

        const importedConfig = data.config as SavedAIConfig;

        // Validate settings structure
        const requiredSettings = [
          "difficulty", "maxThinkingTime", "materialBalance",
          "piecePositioning", "kingSafety", "mobility"
        ];

        for (const key of requiredSettings) {
          if (!(key in importedConfig.settings)) {
            throw new Error(`Invalid configuration: missing ${key} setting.`);
          }
        }

        // Send to server import endpoint
        const response = await apiRequest("/import", {
          method: "POST",
          body: JSON.stringify({
            name: importedConfig.name,
            description: importedConfig.description || "",
            settings: importedConfig.settings,
          }),
        });

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(responseData.error || "Failed to import configuration.");
        }

        console.log(`AI Config imported: "${responseData.config.name}"`);
        resolve(responseData.config);
      } catch (error: any) {
        reject(new Error(error.message || "Failed to parse configuration file."));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read configuration file."));
    };

    reader.readAsText(file);
  });
};

/**
 * Get the count of saved configurations
 */
export const getAIConfigCount = async (): Promise<{ count: number; limit: number; remaining: number }> => {
  try {
    const token = getAuthToken();
    if (!token) {
      return { count: 0, limit: MAX_CONFIGS, remaining: MAX_CONFIGS };
    }

    const response = await apiRequest("/count");

    if (!response.ok) {
      return { count: 0, limit: MAX_CONFIGS, remaining: MAX_CONFIGS };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error getting AI config count:", error);
    return { count: 0, limit: MAX_CONFIGS, remaining: MAX_CONFIGS };
  }
};

/**
 * Format a date string for display
 */
export const formatConfigDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken();
};
